import { Divider, Dropdown, Menu } from 'antd';
import moment from 'moment';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  API_DATE_FORMAT,
  CustomDueDatePicker,
} from 'components/atoms/due-date-picker';
import {
  DateFieldFilterAbsoluteOperationType,
  DateFieldFilterAbsoluteOperationTypes,
  DateFieldFilterOperationType,
  DateFieldFilterRelativeOperationType,
  DateFieldFilterRelativeOperationTypes,
  RelativeDirection,
} from 'components/organisms/tasks/filters/date';
import { absoluteRangePicker } from './absolute-range-picker';
import { Trigger } from './default-trigger';
import styles from './range-picker.module.css';
import { relativeRangePicker } from './relative-range-picker';
import {
  DateRange,
  DefaultRangeOptions,
  getRangeObject,
  getRangeObjectForAbsoluteTypes,
  getRangeObjectForRelativeTypes,
  getValueFromDateRange,
  Range,
} from './types';

const StyledMenu = styled(Menu)`
  max-width: 13rem;
  padding: var(--spacing-04) 0;
`;

const StyledDivider = styled(Divider)`
  margin: var(--spacing-03) 0;
`;

const StyledItemGroup = styled(Menu.ItemGroup)`
  .ant-dropdown-menu-item-group-title {
    text-transform: capitalize;
    opacity: 0.67;
  }
  .ant-dropdown-menu-item-group-list {
    margin: 0;
  }
`;

interface CustomRangePickerProps {
  range: DateRange;
  onDateChange: (range: DateRange) => void;
  rangeOptions?: Range[];
  triggerComponent?: ReactNode;
}

export const CustomRangePicker: FC<CustomRangePickerProps> = ({
  range,
  onDateChange,
  triggerComponent,
  rangeOptions = DefaultRangeOptions,
}) => {
  const [selectedRange, setSelectedRange] = useState<DateRange>(range);
  const [type, setType] = useState<DateFieldFilterOperationType | null>(null);
  const [absoluteDate, setAbsoluteDate] = useState(moment());
  const [relativeDays, setRelativeDays] = useState(0);
  const [isDropdownVisible, setDropdownVisibility] = useState<boolean>(false);
  const [isCustomDatePickerVisible, setCustomDatePickerVisibility] =
    useState<boolean>(false);
  const [isAdvancedPickerVisible, setAdvancedPickerVisibility] =
    useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState<string>(selectedRange.type);
  const date = useMemo(
    () =>
      getValueFromDateRange(selectedRange).map((x) =>
        x.format(API_DATE_FORMAT),
      ) as [string, string],
    [selectedRange],
  );

  useEffect(() => {
    setSelectedRange(range);
  }, [range]);

  useEffect(() => {
    if (isCustomDatePickerVisible) {
      setSelectedMenu(Range.Custom);
    } else {
      setSelectedMenu(selectedRange.type);
    }
  }, [selectedRange, isCustomDatePickerVisible]);

  useEffect(() => {
    if (type !== null && DateFieldFilterAbsoluteOperationTypes.includes(type)) {
      const next = getRangeObjectForAbsoluteTypes(
        type as DateFieldFilterAbsoluteOperationType,
        absoluteDate,
      );

      setSelectedRange(next);
      onDateChange(next);
    }
  }, [absoluteDate, onDateChange, type]);

  useEffect(() => {
    if (type !== null && DateFieldFilterRelativeOperationTypes.includes(type)) {
      const next = getRangeObjectForRelativeTypes(
        type as DateFieldFilterRelativeOperationType,
        relativeDays,
        RelativeDirection.Ago,
      );

      setSelectedRange(next);
      onDateChange(next);
    }
  }, [relativeDays, onDateChange, type]);

  const handleMenuItemClick = useCallback(
    ({ key }) => {
      const isAnything = [
        ...DateFieldFilterRelativeOperationTypes,
        ...DateFieldFilterAbsoluteOperationTypes,
      ].includes(key);

      const next: DateRange = isAnything
        ? DateFieldFilterAbsoluteOperationTypes.includes(key)
          ? getRangeObjectForAbsoluteTypes(key, absoluteDate)
          : DateFieldFilterRelativeOperationTypes.includes(key)
          ? getRangeObjectForRelativeTypes(key, 10, RelativeDirection.Ago)
          : { endDate: moment(), startDate: moment(), type: Range.Advanced }
        : getRangeObject(key);

      if (isAnything) {
        setType(key);
      } else {
        setType(null);
        setDropdownVisibility(false);
      }
      setSelectedRange(next);
      onDateChange(next);
    },
    [absoluteDate, onDateChange],
  );

  const renderCustomPicker = useMemo(
    () => (
      <CustomDueDatePicker
        overlayClassName={styles.range_picker}
        block={false}
        size="small"
        startDate={selectedRange.type === Range.Custom ? date[0] : null}
        hideFooter
        isRangePicker
        dueDate={selectedRange.type === Range.Custom ? date[1] : null}
        onDateChange={([s, d]) => {
          if (s && d) {
            const next = {
              type: Range.Custom,
              startDate: moment(s),
              endDate: moment(d),
            };

            setSelectedRange(next);
            onDateChange(next);
          }
        }}
        renderTrigger={(togglePickerVisibility) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            role="menuitem"
            onClick={() => {
              setCustomDatePickerVisibility((prev) => !prev);
              togglePickerVisibility();
            }}
            className={`ant-dropdown-menu-item ${
              selectedMenu === Range.Custom
                ? 'ant-dropdown-menu-item-selected'
                : ''
            } `}
          >
            <FormattedMessage id="custom" />
          </li>
        )}
      />
    ),
    [date, selectedRange, selectedMenu, onDateChange],
  );

  const renderAdvancedOptionsPicker = useMemo(() => {
    return (
      <>
        <StyledDivider />
        {!isAdvancedPickerVisible ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            role="menuitem"
            onClick={() => {
              setAdvancedPickerVisibility((prev) => !prev);
            }}
            className={`ant-dropdown-menu-item ${
              selectedMenu === Range.Advanced
                ? 'ant-dropdown-menu-item-selected'
                : ''
            } `}
          >
            <FormattedMessage id={Range.Advanced} />
          </li>
        ) : null}
        {isAdvancedPickerVisible ? (
          <>
            <StyledItemGroup title="Relative">
              {DateFieldFilterRelativeOperationTypes.map((_type) =>
                relativeRangePicker({
                  activeType: type,
                  setDays: setRelativeDays,
                  type: _type,
                  days: relativeDays,
                }),
              )}
            </StyledItemGroup>
            <StyledDivider />
            <StyledItemGroup title="Absolute">
              {DateFieldFilterAbsoluteOperationTypes.map((_type) =>
                absoluteRangePicker({
                  activeType: type,
                  type: _type,
                  setDate: setAbsoluteDate,
                  date: absoluteDate,
                }),
              )}
            </StyledItemGroup>
          </>
        ) : null}
      </>
    );
  }, [absoluteDate, isAdvancedPickerVisible, relativeDays, selectedMenu, type]);

  const menu = useMemo(
    () => (
      <StyledMenu onClick={handleMenuItemClick} selectedKeys={[selectedMenu]}>
        {rangeOptions.map((key) =>
          key === Range.Advanced ? (
            <>{renderAdvancedOptionsPicker}</>
          ) : key === Range.Custom ? (
            <>{renderCustomPicker}</>
          ) : (
            <Menu.Item key={key}>
              <FormattedMessage id={key} />
            </Menu.Item>
          ),
        )}
      </StyledMenu>
    ),
    [
      handleMenuItemClick,
      selectedMenu,
      rangeOptions,
      renderAdvancedOptionsPicker,
      renderCustomPicker,
    ],
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={isDropdownVisible}
      onVisibleChange={(isVisible) => {
        setDropdownVisibility(isVisible);
        setCustomDatePickerVisibility(false);
        setAdvancedPickerVisibility(false);
      }}
    >
      <span>
        {triggerComponent != null ? (
          triggerComponent
        ) : (
          <Trigger range={selectedRange} />
        )}
      </span>
    </Dropdown>
  );
};
